import { WarningAttachmentsCanNotBeDeleted } from 'owa-locstrings/lib/strings/warningattachmentscannotbedeleted.locstring.json';
import loc, { format } from 'owa-localize';
import { onSmimeModeEnabled } from '../actions/smimeActions';
import { ComposeViewState } from 'owa-mail-compose-store';
import { infoBarAddHandler, infoBarRemoveHandler } from '../utils/infoBarHandlers';
import upConvertHelper from '../utils/onUpconvertHelper';
import isAttachmentPartOfItem from 'owa-attachment-full-data/lib/utils/isAttachmentPartOfItem';
import { ClientAttachmentId } from 'owa-attachment-model-store';
import { default as deleteAttachmentFromStore } from 'owa-attachment-model-store/lib/actions/deleteAttachment';
import deleteAttachment from 'owa-attachment-well-data/lib/services/deleteAttachment';
import { getUserMailboxInfo } from 'owa-client-ids';
import DeleteAttachmentResponseMessage from 'owa-service/lib/contract/DeleteAttachmentResponseMessage';
import itemId from 'owa-service/lib/factory/itemId';

import { trace } from 'owa-trace';
import { orchestrator } from 'satcheljs';
import {
    AttachmentState,
    AttachmentType,
    lazyGetAllValidAttachments,
} from 'owa-attachment-well-data';
import {
    lazyCreateSmimeAttachmentsViaSmimeQueueManager,
    lazyInitializeAttachmentStateForSmimeAttachments,
} from 'owa-smime';

/**
 * Updates SmimeViewState with admin/user settings in new compose.
 */
export default orchestrator(onSmimeModeEnabled, async actionMessage => {
    const { composeViewState } = actionMessage;
    const attachments: AttachmentState[] = (await lazyGetAllValidAttachments.import())(
        composeViewState.attachmentWell,
        composeViewState.content,
        composeViewState.bodyType,
        false /*filterSmimeAttachment */
    );
    composeViewState.isInlineCompose && upConvertHelper(composeViewState);

    // Don't create queue if attachments are not present
    if (!attachments.length) {
        return;
    }
    try {
        const initializeAttachmentStateForSmimeAttachments = await lazyInitializeAttachmentStateForSmimeAttachments.import();
        initializeAttachmentStateForSmimeAttachments(composeViewState.attachmentWell, attachments);
        const createSmimeAttachmentsViaSmimeQueueManager = await lazyCreateSmimeAttachmentsViaSmimeQueueManager.import();
        /**
         * itemId/composeId is used as a key in a hash-table for SmimeAttachmentDownloadQueueManager.
         * The hash-table is required because we can have multiple instances of queue managers for different compose forms that are open.
         * We need a key to associate the queue manager with the compose form.
         */
        const parentItemId = composeViewState.itemId || itemId({ Id: composeViewState.composeId });
        createSmimeAttachmentsViaSmimeQueueManager(
            composeViewState.attachmentWell,
            { ...parentItemId, mailboxInfo: getUserMailboxInfo() },
            infoBarAddHandler(composeViewState) /* infoBarAddHandler */,
            infoBarRemoveHandler(composeViewState) /* infoBarRemoveHandler */,
            attachments,
            (attachment: AttachmentType) =>
                tryDeleteAttachmentsOnDownloadSuccess(attachment, composeViewState)
        );
    } catch (error) {
        trace.error('SmimeTraceError: onSmimeModeEnabled failed' + error.message);
    }
});

async function tryDeleteAttachmentsOnDownloadSuccess(
    attachment: AttachmentType,
    viewState: ComposeViewState
): Promise<void> {
    const attachmentToDelete = attachment.AttachmentId as ClientAttachmentId;

    if (!isAttachmentPartOfItem(attachment, viewState.itemId)) {
        /**
         * This occurs for the following conditions:
         * 1. Reply/ReplyAll/Forward a non-S/MIME mail with attachments
         * 2. S/MIME admin/user settings are on
         */
        return;
    }

    // Remove from store
    deleteAttachmentFromStore(attachmentToDelete);

    // Delete from server
    try {
        const response = await deleteAttachment(attachmentToDelete);
        const responseMessage: DeleteAttachmentResponseMessage = response.ResponseMessages.Items[0];
        if (responseMessage.ResponseClass === 'Success') {
            trace.info('Attachment deleted successfully on toggle of S/MIME mode');
        } else {
            throw new Error(
                `Delete Smime Attachment toggle: Failed to delete attachment with ResponseClass= ${
                    responseMessage.ResponseClass
                }, ResponseCode=${responseMessage.ResponseCode}`
            );
        }
    } catch (error) {
        trace.error('SmimeTraceError: ' + error.message);
        infoBarAddHandler(viewState)('WarningAttachmentCanNotBeDeleted', [
            format(loc(WarningAttachmentsCanNotBeDeleted), attachment.Name),
        ]);
    }
}
